import { useCallback, useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useMutation } from '@apollo/client';

import useUser from 'hooks/use-user';
import useErnie from 'shared/hooks/use-ernie';
import { useSearchEnterprise } from 'src/enterprise/hooks/use-search-enterprise';
import { useGetDutchiePosLoyaltyConfig } from 'src/components/loyalty/use-get-dutchie-pos-loyalty-config';
import {
  CreateEnterpriseDutchiePosLoyaltyMemberV2Document,
  CreateDutchiePosLoyaltyMemberV2Document,
} from 'src/gql/graphql';
import { getDefaultRewardsProgramDisplayName } from 'shared/core/helpers/dispensaries';
import { useIrlEventParams } from './use-irl-event-params';
import { formatEnterpriseName } from '../utils/format-enterprise-name';

export type CreateEnterpriseDposCustomerParams = {
  isEnrollingLoyalty: boolean;
};

export type CreateEnterpriseDposCustomerResult = {
  posCustomerId: string | null;
  isLoyaltyMember: boolean | null;
};

export type UseCreateEnterpriseDposCustomerReturn = {
  createEnterpriseDposCustomer: (
    params: CreateEnterpriseDposCustomerParams
  ) => Promise<CreateEnterpriseDposCustomerResult | null>;
  loading: boolean;
  called: boolean;
  userFirstName: string;
  rewardsProgramName: string;
  enterpriseName: string;
};

/**
 * Hook to create a DPOS customer record.
 * Dispensary path (dispensaryId): uses createDutchiePosLoyaltyMemberV2
 * Enterprise path (enterpriseId + isoRegionCode): uses createEnterpriseDutchiePosLoyaltyMemberV2
 * Both paths return the same LoyaltyMemberResponse shape ({ posCustomerId, isLoyaltyMember })
 */
export function useCreateEnterpriseDposCustomer(): UseCreateEnterpriseDposCustomerReturn {
  const { enterpriseId, isoRegionCode, dispensaryId } = useIrlEventParams();
  const isDispensaryPath = !!dispensaryId;

  const { enterprise } = useSearchEnterprise({ enterpriseId });
  const showErnie = useErnie();
  const user = useUser();

  const { data: dispensaryLoyaltyConfig } = useGetDutchiePosLoyaltyConfig({
    dispensaryId,
    skip: !isDispensaryPath,
  });

  const { isLoggedIn, userId, userFirstName } = useObserver(() => ({
    isLoggedIn: user.isLoggedIn ?? false,
    userId: user.id,
    userFirstName: user.firstName ?? '',
  }));

  const rewardsProgramName = useMemo(() => {
    if (isDispensaryPath) {
      return dispensaryLoyaltyConfig?.onlineName ?? 'rewards program';
    }
    return enterprise?.displayName ?? getDefaultRewardsProgramDisplayName({ location: { isoRegionCode } });
  }, [isDispensaryPath, dispensaryLoyaltyConfig?.onlineName, enterprise?.displayName, isoRegionCode]);

  const enterpriseName = useMemo(() => {
    if (isDispensaryPath) {
      return dispensaryLoyaltyConfig?.onlineName ?? 'Company';
    }
    if (enterprise?.name) {
      return formatEnterpriseName(enterprise.name);
    }
    return enterprise?.displayName ?? 'Company';
  }, [isDispensaryPath, dispensaryLoyaltyConfig?.onlineName, enterprise?.name, enterprise?.displayName]);

  const [createEnterpriseMutation, { loading: enterpriseLoading, called: enterpriseCalled }] = useMutation(
    CreateEnterpriseDutchiePosLoyaltyMemberV2Document
  );

  const [createDispensaryMutation, { loading: dispensaryLoading, called: dispensaryCalled }] = useMutation(
    CreateDutchiePosLoyaltyMemberV2Document
  );

  const loading = isDispensaryPath ? dispensaryLoading : enterpriseLoading;
  const called = isDispensaryPath ? dispensaryCalled : enterpriseCalled;

  const createEnterpriseDposCustomer = useCallback(
    async ({
      isEnrollingLoyalty,
    }: CreateEnterpriseDposCustomerParams): Promise<CreateEnterpriseDposCustomerResult | null> => {
      if (
        !isLoggedIn ||
        !userId ||
        (isDispensaryPath && !dispensaryId) ||
        (!isDispensaryPath && (!enterpriseId || !isoRegionCode))
      ) {
        return null;
      }

      try {
        let posCustomerId: string | null = null;
        let isLoyaltyMember: boolean | null = null;

        if (isDispensaryPath) {
          const { data } = await createDispensaryMutation({
            variables: { dispensaryId, userId, isLoyaltyMember: isEnrollingLoyalty, useStrictMatching: true },
          });
          ({ posCustomerId = null, isLoyaltyMember = null } = data?.createDutchiePosLoyaltyMemberV2 ?? {});
        } else {
          const { data } = await createEnterpriseMutation({
            variables: {
              enterpriseId,
              userId,
              isoRegionCode,
              isLoyaltyMember: isEnrollingLoyalty,
              useStrictMatching: true,
            },
          });
          ({ posCustomerId = null, isLoyaltyMember = null } = data?.createEnterpriseDutchiePosLoyaltyMemberV2 ?? {});
        }

        if (isEnrollingLoyalty) {
          if (posCustomerId) {
            showErnie(`Welcome to ${rewardsProgramName}!`, 'success');
          } else {
            showErnie(`Error enrolling in ${rewardsProgramName}. Please try again.`, 'danger');
          }
        }

        return { posCustomerId, isLoyaltyMember };
      } catch (error) {
        console.error('Error creating DPOS customer:', error);

        if (isEnrollingLoyalty) {
          showErnie(`Error enrolling in ${rewardsProgramName}. Please try again.`, 'danger');
        }

        return null;
      }
    },
    [
      isLoggedIn,
      userId,
      isDispensaryPath,
      dispensaryId,
      enterpriseId,
      isoRegionCode,
      createDispensaryMutation,
      createEnterpriseMutation,
      showErnie,
      rewardsProgramName,
    ]
  );

  return {
    createEnterpriseDposCustomer,
    loading,
    called,
    userFirstName,
    rewardsProgramName,
    enterpriseName,
  };
}
